/**
 * OscopLogo — Logo SVG officiel O'SCOP Outremer.
 *
 * Concept : un anneau or institutionnel avec un soleil/étoile à 8 branches au
 * centre (8 branches = symbolique des territoires ultramarins), évoque le réseau
 * coopératif ESS rayonnant depuis l'Outre-Mer. Variant `dark` adapté aux fonds
 * navy (anneau or + étoile claire), variant `light` adapté aux fonds clairs.
 *
 * Réutilisable partout via :
 *   <OscopLogo size={48} variant="on-navy" />
 */
import React from 'react';

const VARIANTS = {
  // Sur fond navy O'SCOP : anneau or, étoile blanche/or clair
  'on-navy': {
    ring: '#DEA900',
    ringGlow: '#f5cc3e',
    star: '#FFFFFF',
    starAccent: '#f5cc3e',
    dot: '#DEA900',
    bg: 'transparent',
  },
  // Sur fond clair : anneau navy, étoile or
  'on-light': {
    ring: '#0a3d62',
    ringGlow: '#0c4a78',
    star: '#DEA900',
    starAccent: '#b88600',
    dot: '#0a3d62',
    bg: 'transparent',
  },
  // Monochrome or (pour micro-affichages)
  'gold': {
    ring: '#DEA900',
    ringGlow: '#DEA900',
    star: '#DEA900',
    starAccent: '#DEA900',
    dot: '#DEA900',
    bg: 'transparent',
  },
};

export default function OscopLogo({ size = 40, variant = 'on-navy', className = '', strokeWidth = 2.2, withMonogram = false, title = "O'SCOP Outremer", animated = true }) {
  const c = VARIANTS[variant] || VARIANTS['on-navy'];
  const uid = `${variant}-${size}`;

  // SVG 64×64 viewBox, scalé via size
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title}
      className={`oscop-logo ${animated ? 'oscop-logo-animated' : ''} ${className}`}
    >
      <title>{title}</title>
      <style>{`
        .oscop-logo .oscop-star { transform-origin: 32px 32px; transition: transform 700ms cubic-bezier(0.4, 0, 0.2, 1); }
        .oscop-logo .oscop-ring { transition: filter 600ms ease; }
        .oscop-logo .oscop-dot { transform-origin: 13.6px 13.6px; transition: transform 600ms ease, filter 600ms ease; }
        @keyframes oscop-star-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes oscop-dot-pulse { 0%,100% { transform: scale(1); filter: drop-shadow(0 0 0 transparent); } 50% { transform: scale(1.4); filter: drop-shadow(0 0 4px ${c.ringGlow}); } }
        /* Animation au survol d'un parent (group/cooper-card, group/header, etc.) ou du SVG lui-même */
        .oscop-logo-animated:hover .oscop-star,
        .group:hover .oscop-logo-animated .oscop-star,
        .group\\/header:hover .oscop-logo-animated .oscop-star {
          animation: oscop-star-spin 6s linear infinite;
        }
        .oscop-logo-animated:hover .oscop-ring,
        .group:hover .oscop-logo-animated .oscop-ring,
        .group\\/header:hover .oscop-logo-animated .oscop-ring {
          filter: drop-shadow(0 0 6px ${c.ringGlow});
        }
        .oscop-logo-animated:hover .oscop-dot,
        .group:hover .oscop-logo-animated .oscop-dot,
        .group\\/header:hover .oscop-logo-animated .oscop-dot {
          animation: oscop-dot-pulse 2.4s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .oscop-logo-animated:hover .oscop-star,
          .oscop-logo-animated:hover .oscop-dot { animation: none; }
        }
      `}</style>
      <defs>
        <linearGradient id={`oscop-ring-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={c.ringGlow} stopOpacity="1" />
          <stop offset="100%" stopColor={c.ring} stopOpacity="1" />
        </linearGradient>
        <radialGradient id={`oscop-glow-${uid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={c.starAccent} stopOpacity="0.35" />
          <stop offset="70%" stopColor={c.starAccent} stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Halo subtil */}
      <circle cx="32" cy="32" r="30" fill={`url(#oscop-glow-${uid})`} />

      {/* Anneau extérieur (ouvert en bas — évoque la lettre O + ouverture) */}
      <path
        className="oscop-ring"
        d="M 32 6 a 26 26 0 1 1 -18.38 7.62"
        fill="none"
        stroke={`url(#oscop-ring-${uid})`}
        strokeWidth={strokeWidth + 0.8}
        strokeLinecap="round"
      />

      {/* Point d'ouverture (or institutionnel) — accent visuel pulsant au survol */}
      <circle className="oscop-dot" cx="13.6" cy="13.6" r={strokeWidth + 0.5} fill={c.ring} />

      {/* Étoile 8-branches centrale (symbole DOM-TOM rayonnant) */}
      <g className="oscop-star" transform="translate(32 32)">
        {/* 4 branches longues : cardinales (N/S/E/O) */}
        <path
          d="M 0 -14 L 1.6 -2 L 0 0 L -1.6 -2 Z
             M 14 0 L 2 1.6 L 0 0 L 2 -1.6 Z
             M 0 14 L -1.6 2 L 0 0 L 1.6 2 Z
             M -14 0 L -2 -1.6 L 0 0 L -2 1.6 Z"
          fill={c.star}
        />
        {/* 4 branches courtes : intercardinales (NE/SE/SO/NO) */}
        <path
          d="M 8.5 -8.5 L 2 -1.2 L 0 0 L 1.2 -2 Z
             M 8.5 8.5 L 1.2 2 L 0 0 L 2 1.2 Z
             M -8.5 8.5 L -2 1.2 L 0 0 L -1.2 2 Z
             M -8.5 -8.5 L -1.2 -2 L 0 0 L -2 -1.2 Z"
          fill={c.starAccent}
          opacity="0.85"
        />
        {/* Centre */}
        <circle r="2.2" fill={c.dot} />
      </g>

      {/* Optional monogramme "OS" intégré (off par défaut) */}
      {withMonogram && (
        <text
          x="32"
          y="58"
          textAnchor="middle"
          fontFamily="ui-sans-serif, system-ui"
          fontWeight="800"
          fontSize="8"
          letterSpacing="0.5"
          fill={c.ring}
        >
          O'SCOP
        </text>
      )}
    </svg>
  );
}
